/**
 * Loading and locating the child forms of form-container controls.
 *
 * FormRenderer calls these for every form-container on the page, so a child form's schema is known
 * even when its container is not mounted (inside a collapsed panel, or an unvisited step). Submit
 * validation depends on that.
 */

import type { FormControl, FormSchema } from '@atlas-forms/types-js';
import { FormDefinitionApiClient, getAtlasApiConfig } from '@atlas-forms/client-js';
import type { DataBindingConfig } from '@atlas-forms/controls-form-container-react';
import { nestedControls } from './nestedControls';

export interface ChildFormRef {
  /** Registry scope: the id of the form-container control */
  scope: string;
  formID: string;
  binding: DataBindingConfig;
}

/** Every child form of every form-container in `controls`, at any depth */
export function containerChildFormRefs(controls: FormControl[]): ChildFormRef[] {
  const refs: ChildFormRef[] = [];
  const visit = (list: FormControl[]) => {
    for (const control of list) {
      if (control.type === 'form-container') {
        const childForms = (control.config?.childForms as Array<{ formID: string; dataBinding?: DataBindingConfig }> | undefined) ?? [];
        for (const child of childForms) {
          refs.push({
            scope: control.id,
            formID: child.formID,
            binding: child.dataBinding ?? ({ mode: 'independent' } as DataBindingConfig),
          });
        }
      }
      visit(nestedControls(control));
    }
  };
  visit(controls);
  return refs;
}

/** Extract the slice of master data that a child form should see */
export function resolveChildData(
  master: Record<string, unknown>,
  binding: DataBindingConfig
): Record<string, unknown> {
  if (binding.mode === 'sub-root') {
    // A sub-root binding saved without a path (rootPath missing) has no slice to read
    const rootPath = binding.rootPath ?? '';
    if (!rootPath) return {};
    const val = rootPath.split('.').reduce<unknown>((obj, key) => {
      if (obj && typeof obj === 'object') return (obj as Record<string, unknown>)[key];
      return undefined;
    }, master);
    if (val && typeof val === 'object' && !Array.isArray(val)) {
      return val as Record<string, unknown>;
    }
    // Path doesn't exist — return empty object (and optionally create the path)
    if (binding.createRootIfMissing !== false) {
      const initial = binding.rootInitialValue ?? {};
      return typeof initial === 'object' && !Array.isArray(initial)
        ? (initial as Record<string, unknown>)
        : {};
    }
    return {};
  }
  if (binding.mode === 'independent') return {};
  // shared-root: child sees the full master
  return master;
}

/**
 * Immutably set a value at a dot-separated path.
 * Each level is shallow-cloned so no part of the original tree is mutated.
 */
export function setAtPath(
  base: Record<string, unknown>,
  path: string,
  value: unknown
): Record<string, unknown> {
  const [head, ...rest] = path.split('.');
  if (rest.length === 0) {
    return { ...base, [head]: value };
  }
  const nested =
    base[head] && typeof base[head] === 'object' && !Array.isArray(base[head])
      ? (base[head] as Record<string, unknown>)
      : {};
  return { ...base, [head]: setAtPath(nested, rest.join('.'), value) };
}

/** Upper bound on pages read while looking for a name (50 per page) */
const MAX_NAME_LOOKUP_PAGES = 20;

/**
 * Resolve a form name to its numeric id through the list endpoint (exact Name match).
 * Pages through the results, since the title search can return more than one page of matches.
 * Returns null when no form has that name.
 */
async function findFormIdByName(client: FormDefinitionApiClient, name: string): Promise<number | null> {
  for (let page = 1; page <= MAX_NAME_LOOKUP_PAGES; page++) {
    const result = await client.listForms({
      SearchTitle: name,
      PageInfo: { PageNumber: page, PageSize: 50 },
    });
    if (!result.success || !result.data) return null;
    const raw = result.data as unknown as {
      Data?: unknown[];
      data?: unknown[];
      PageInfo?: { TotalPages?: number };
    };
    const rows = (raw.Data ?? raw.data ?? []) as Array<{ FormID?: number; formID?: number; Name?: string; name?: string }>;
    const match = rows.find((row) => (row.Name ?? row.name) === name);
    if (match) {
      const id = match.FormID ?? match.formID;
      return typeof id === 'number' ? id : null;
    }
    const totalPages = raw.PageInfo?.TotalPages ?? 0;
    if (rows.length === 0 || page >= totalPages) break;
  }
  return null;
}

/**
 * A child form that could not be loaded. It stays unknown to the registry, so submit and step
 * changes report it as unavailable instead of passing it. The reason is logged for support.
 */
function reportUnloaded(formID: string, reason: unknown): void {
  console.warn(`[childFormLoader] child form ${formID} could not be loaded; it will block validation`, reason);
}

/**
 * Fetch the schemas of the given form ids (numeric ids or names). Each form is fetched on its own,
 * so one failure does not stop the others. A form that fails is missing from the result, and its
 * children stay unknown, which blocks submit.
 */
export async function loadChildFormSchemas(formIDs: string[]): Promise<Map<string, FormSchema>> {
  const cfg = getAtlasApiConfig();
  const client = new FormDefinitionApiClient({ baseUrl: cfg.baseUrl });
  const loaded = new Map<string, FormSchema>();

  await Promise.all(
    formIDs.map(async (formID) => {
      try {
        // The by-id endpoint only takes numeric ids, so a name is resolved to its id first
        const numericId = /^\d+$/.test(formID) ? Number(formID) : await findFormIdByName(client, formID);
        if (numericId === null) {
          reportUnloaded(formID, 'no form has this name');
          return;
        }
        const result = await client.getFormById(numericId);
        if (!result.success || !result.data) {
          reportUnloaded(formID, 'the server did not return the form');
          return;
        }
        const form = (result.data as any).form ?? result.data;
        const schemaRaw = form?.Schema ?? form?.schema;
        if (!schemaRaw) {
          reportUnloaded(formID, 'the form has no schema');
          return;
        }
        loaded.set(formID, typeof schemaRaw === 'string' ? JSON.parse(schemaRaw) : schemaRaw);
      } catch (error) {
        reportUnloaded(formID, error);
      }
    })
  );

  return loaded;
}
